// =============================================================================
// MessageBubble.tsx — Individual chat message bubble (web port)
// =============================================================================

import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { useTheme } from '../../context/PermissionsContext/ThemeContext/ThemeContext';
import type { ChatMessage } from '../../services/notesToHank/NotesToHankTypes';
import QuestionButtons from './QuestionButtons';
import ActionButtons from './ActionButtons';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface MessageBubbleProps {
  message: ChatMessage;
  isUser: boolean;
  onAnswerSelect?: (
    questionIndex: number,
    answerIndex: number,
    answerId: string,
    answerText: string,
    questionId?: string
  ) => void;
  onBatchSubmit?: (
    answerIds: string[],
    combinedText: string,
    selectedAnswersMap: Record<string, string>
  ) => void;
  onActionPress?: (
    actionId: string,
    label: string,
    clientCommand?: string
  ) => void;
  pressedActionButtonIds?: Set<string>;
  questionAttachments?: Record<
    string,
    import('../../services/notesToHank/NotesToHankTypes').AttachmentItem[]
  >;
  onRemoveQuestionAttachment?: (questionId: string, index: number) => void;
  onRetryUpload?: (questionId: string, attachmentIndex: number) => void;
  isUploading?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatTimestamp = (ts: number): string => {
  const date = new Date(ts);
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isUser,
  onAnswerSelect,
  onBatchSubmit,
  onActionPress,
  pressedActionButtonIds,
  questionAttachments,
  onRemoveQuestionAttachment,
  onRetryUpload,
  isUploading,
}) => {
  const { theme } = useTheme();
  const timeLabel = formatTimestamp(message.timestamp);
  const senderLabel = isUser ? 'You' : 'AI';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        my: 0.5,
        px: 1.5,
      }}
      aria-label={`${senderLabel}, ${timeLabel}`}
    >
      <Box
        sx={{
          maxWidth: '80%',
          borderRadius: '12px',
          p: 1.5,
          backgroundColor: isUser ? theme.primary : theme.background2,
        }}
      >
        {/* Message text */}
        <Typography
          variant="body2"
          sx={{
            color: isUser ? theme.textCTA : theme.text,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {message.text}
        </Typography>

        {/* Photo thumbnails */}
        {message.photos && message.photos.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              mt: 1,
              gap: 0.75,
            }}
          >
            {message.photos.map((photo, idx) => (
              <Box
                key={`photo-${idx}`}
                component="img"
                src={photo.uri}
                alt={`Attachment photo ${idx + 1}`}
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '6px',
                  objectFit: 'cover',
                }}
              />
            ))}
          </Box>
        )}

        {/* File attachments */}
        {message.attachments && message.attachments.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              mt: 1,
              gap: 0.75,
            }}
          >
            {message.attachments.map((att, idx) =>
              att.type === 'photo' ? (
                <Box
                  key={`att-${idx}`}
                  component="img"
                  src={att.uri}
                  alt={`Attachment ${idx + 1}`}
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '6px',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <Chip
                  key={`att-${idx}`}
                  label={att.fileName ?? 'File'}
                  size="small"
                  sx={{
                    backgroundColor: theme.background3,
                    color: theme.text,
                  }}
                />
              )
            )}
          </Box>
        )}

        {/* AI-only: inline question buttons */}
        {!isUser &&
          message.questions &&
          message.questions.length > 0 &&
          onAnswerSelect && (
            <QuestionButtons
              questions={message.questions}
              onAnswerSelect={onAnswerSelect}
              onBatchSubmit={onBatchSubmit}
              selectedAnswers={message.selectedAnswers ?? {}}
              questionAttachments={questionAttachments}
              onRemoveQuestionAttachment={onRemoveQuestionAttachment}
              onRetryUpload={onRetryUpload}
              isUploading={isUploading}
            />
          )}

        {/* AI-only: inline action buttons */}
        {!isUser &&
          message.actionButtons &&
          message.actionButtons.length > 0 &&
          onActionPress && (
            <ActionButtons
              actions={message.actionButtons}
              onPress={onActionPress}
              pressedIds={pressedActionButtonIds ?? new Set<string>()}
            />
          )}

        {/* Timestamp */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.5,
            color: isUser ? theme.textCTA : theme.textLight,
            textAlign: isUser ? 'right' : 'left',
            opacity: 0.7,
          }}
        >
          {timeLabel}
        </Typography>
      </Box>
    </Box>
  );
};

export default MessageBubble;
